import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { 
  fetchPaletteFolders, fetchComponentsForPalette, fetchComponentsForPaletteFolder,
  fetchMediaForPalette
} from 'actions/palette';
import { createWidget } from 'actions/editor';

import cx from 'classnames';
import _ from 'lodash';
import { Nav, Tab, TabContainer, Button, Image, Fade } from 'react-bootstrap';
import { IKImage } from 'imagekitio-react';

import { Widgets } from '@paperflite-marketing/cleverstory-commons';

import { ReactSVG } from 'react-svg'

import { PaletteContext  } from '.';
import InfiniteScroll from "components/InfiniteScroll";
import { getPositionForNewWidget } from 'components/Editor/helper/layoutManager';

import { SHAPE, RECTANGLE, LINE, IMAGE } from 'constants/widgetTypes';

import styles from './palette.module.scss';
import { getStoryboardMixpanelEventPayload } from 'services/mixpanelUtil';
import { MIXPANEL_WIDGET_ADDED, MIXPANEL_WIDGET_USED } from 'constants/mixpanelEvents';
import { createMixpanelEvent } from 'actions/mixpanel';
import StoryboardContext from 'components/Storyboard/context';
import { ShortLoader } from 'components/ApplicationLoader';

export const ShapePalette = ({ editorSession, defaultParent }) => {
  const paletteContext = React.useContext(PaletteContext);

  const { palettes = [] } = paletteContext;

  const paletteData = palettes.find(item => item.type === 'ICONS');
  const defaultId = _.get(paletteData, 'defaultId', null);
  const brandkitId = _.get(paletteData, 'brandkitId', null);

  return (
    <Tab.Pane eventKey="ICONS" className={cx(styles.tabPane, styles.shapePalette)}>
      <ShapePaletteFolders
        editorSession={editorSession}
        defaultParent={defaultParent}
        paletteId={defaultId}
        brandkitId={brandkitId}
        defaultId={defaultId}
        type={'default'}
        paletteData={paletteData}
      />
    </Tab.Pane>
  )
}

const ShapePaletteFolders = ({ editorSession, defaultParent, paletteId, brandkitId, defaultId, type, paletteData }) => {
  const dispatch = useDispatch();
  const iconPaletteRef = React.useRef(null);

  const folders = useSelector(state => _.get(state, `palette.default[${paletteId}].folders.items`, []));
  const fetched = useSelector(state => _.get(state, `palette.default[${paletteId}].folders.fetched`, false));
  const loading = useSelector(state => _.get(state, `palette.default[${paletteId}].folders.loading`, false));
  const defaultLoading = useSelector(state => { return _.get(state, `palette.default[${defaultId}].loading`) || false });

  const mediaList = useSelector(state => { return _.get(state, `palette.brandkit[${brandkitId}].image.items`) || [] });
  const mediaFetched = useSelector(state => _.get(state, `palette.brandkit[${brandkitId}].image.fetched`, {}));

  const workspace = useSelector((state) => _.get(state, "workspaces.workspace.item"));
  const brandkitEnabled = _.get(workspace, 'brandkitEnabled', false);

  const showDefault = _.get(paletteData, 'showDefault', false) || !brandkitEnabled;

  useEffect(() => {
    if (paletteId && !fetched) {
      dispatch(fetchPaletteFolders(paletteId, type));
      handleComponentsForPalette(0);
    }
  }, [paletteId]);

  const handleComponentsForPalette = (page) => {
    var request = {
      sort: 'order',
      order: 'ASC'
    }
    dispatch(fetchComponentsForPalette(paletteId, type, request, page, 50));
  }

  const handleFolderSelect = (folder) => {
    const folderElement = document.getElementById(folder);
    if (folderElement) {
      folderElement.scrollIntoView({ behavior: 'smooth' });
    }
  }

  if(loading || defaultLoading || !mediaFetched) return (
    <div className={styles.paletteLoaderContainer}>
      <ShortLoader className={styles.paletteLoaderContainer}/>
    </div>
  );

  return (
    <div className={styles.iconManagement}>
      <div className={styles.iconFolders}>
        {mediaList.length > 0 && brandkitEnabled &&
          <div className={styles.headerTitle}>BRAND ICONS</div>
        }
        
        <Nav variant="pills" className={styles.iconManagementTab}>
          {mediaList.length > 0 && brandkitEnabled &&
            <>
              <Nav.Item key={'brandIcons'} onClick={() => { handleFolderSelect('brand-icons') }}>
                <Nav.Link eventKey={'brandIcons'}>Brand Icons</Nav.Link>
              </Nav.Item>

              <hr />  
            </>
          }

        {showDefault && 
          <>
            <div className={styles.headerTitle}>CLEVERSTORY</div>
            {folders.map(folder => {
              return (
                <Nav.Item key={folder.id} onClick={() => { handleFolderSelect(folder.id) }}>
                  <Nav.Link eventKey={folder.id}>{folder.name}</Nav.Link>
                </Nav.Item>
              )
            })}
          </>
        }
        </Nav>
      </div>
      <div className={styles.iconContent} ref={iconPaletteRef}>
        {mediaList.length > 0 && brandkitEnabled &&
          <div className={cx(styles.paletteTitle, styles.shapeTitle)} id='brand-icons'>
            <h3 className={styles.label}>BRAND ICONS</h3>
            <hr className={styles.labelHighliter} />
          </div>
        }
       { brandkitEnabled &&
        <BrandIconPaletteView
          editorSession={editorSession}
          defaultParent={defaultParent}
          palette={brandkitId}
        />
        }

        {showDefault && 
          <>
            <div className={cx(styles.paletteTitle, styles.shapeTitle)}>
              <h3 className={styles.label}>CLEVERSTORY</h3>
              <hr className={styles.labelHighliter} />
            </div>
            {folders.map(folder => {
              return (
                <ShapePaletteView
                  key={folder.id}
                  editorSession={editorSession}
                  defaultParent={defaultParent}
                  paletteId={paletteId}
                  folder={folder}
                  type={type}
                />
              )
            })}
          </>
        }
      </div>
    </div>
  )
}

const ShapePaletteView = ({ editorSession, defaultParent, paletteId, folder, type }) => {
  const dispatch = useDispatch();
  const paletteContext = React.useContext(PaletteContext);
  const storyboardContext = React.useContext(StoryboardContext);

  const folderId = _.get(folder, 'id');

  const components = useSelector(state => _.get(state, `palette.${type}[${paletteId}].folders[${folder.id}].items`, []));
  const fetched = useSelector(state => _.get(state, `palette.${type}[${paletteId}].folders[${folder.id}].fetched`, false));
  const loading = useSelector(state => _.get(state, `palette.${type}[${paletteId}].folders[${folder.id}].loading`, false));
  const pagination = useSelector(state => _.get(state, `palette.${type}[${paletteId}].folders[${folder.id}].pagination`, {}));
  const hasMoreItems = _.get(pagination, "currentPage") < _.get(pagination, "totalPages") - 1;

  const widgetType = _.get(components, '[0].content.widgets[0].type');

  const parentWidget = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || []
    return widgets.find(widget => {
      return widget.uid === defaultParent
    });
  });

  const desktopParentLayout = _.get(parentWidget, `properties.desktop.layout`);
  const mobileParentLayout = _.get(parentWidget, `properties.mobile.layout`);

  useEffect(() => {
    if (paletteId && folderId && !fetched) {
      handleFetchComponentsForFolder(0);
    }
  }, [paletteId, folderId]);

  const handleFetchComponentsForFolder = (page) => {
    var request = {
      sort: 'order',
      order: 'ASC'
    }
    dispatch(fetchComponentsForPaletteFolder(paletteId, folderId, type, request, page, 10));
  }

  const loadMore = () => {
    const nextPage = pagination.currentPage + 1;
    if (nextPage >= pagination.totalPages || loading) {
      return;
    }
    handleFetchComponentsForFolder(nextPage);
  }

  const handleCreateLine = (data) => {
    const desktopPosition = getPositionForNewWidget(editorSession, desktopParentLayout, { width: 600, height: 50 });
    const mobilePosition = getPositionForNewWidget(editorSession, mobileParentLayout, { width: 100, height: 50 });

    dispatch(
      createWidget(
        editorSession,
        [
          {
            type: LINE,
            name: "Line",
            properties: {
              ...data.properties,
              desktop: {
                ...data.properties.desktop,
                visible: true,
                layout: {
                  ...data.properties.desktop.layout,
                  transform: {
                    translate: `${desktopPosition.x}px, ${desktopPosition.y}px`,
                    rotate: "0deg"
                  }
                }
              },
              mobile: {
                ...data.properties.mobile,
                visible: true,
                layout: {
                  ...data.properties.mobile.layout,
                  transform: {
                    translate: `${mobilePosition.x}px, ${mobilePosition.y}px`,
                    rotate: "0deg",
                  }
                }
              }
            }
          }
        ],
        defaultParent
      )
    );
    var mixpanelPayload = getStoryboardMixpanelEventPayload(storyboardContext.id, storyboardContext.name, storyboardContext.type);
    mixpanelPayload = {
      ...mixpanelPayload,
      widget_type: 'Icon',
      brandkit_widget: false
    }
    dispatch(createMixpanelEvent(MIXPANEL_WIDGET_USED, mixpanelPayload))

    paletteContext.close();
  }

  const handleCreateShape = (data, type) => {
    const desktopPosition = getPositionForNewWidget(editorSession, desktopParentLayout, { width: 600, height: 50 });
    const mobilePosition = getPositionForNewWidget(editorSession, mobileParentLayout, { width: 100, height: 50 });

    dispatch(
      createWidget(
        editorSession,
        [
          {
            type: type,
            name: "Shape",
            properties: {
              ...data.properties,
              desktop: {
                ...data.properties.desktop,
                layout: {
                  ...data.properties.desktop.layout,
                  transform: {
                    translate: `${desktopPosition.x}px, ${desktopPosition.y}px`,
                    rotate: "0deg"
                  }
                }
              },
              mobile: {
                ...data.properties.mobile,
                layout: {
                  ...data.properties.mobile.layout,
                  transform: {
                    translate: `${mobilePosition.x}px, ${mobilePosition.y}px`,
                    rotate: "0deg",
                  }
                },
              },
            },
            content: data.content,
          },
        ],
        defaultParent
      )
    );
    var mixpanelPayload = getStoryboardMixpanelEventPayload(storyboardContext.id, storyboardContext.name, storyboardContext.type);
    mixpanelPayload = {
      ...mixpanelPayload,
      widget_type: 'Icon',
      brandkit_widget: false
    }
    dispatch(createMixpanelEvent(MIXPANEL_WIDGET_USED, mixpanelPayload))

    paletteContext.close();
  }

  return (
    <div className={styles.shapesContainer} id={folderId}>
      <div className={styles.header}>{folder.name}</div>
      <InfiniteScroll
        className={cx(styles.scrollable)}
        loading={loading}
        hasMore={hasMoreItems}
        loadMore={loadMore}
      >
        <div className={cx(styles.lines, { [styles.grid]: widgetType !== 'Lines'})}>
          {components.map((component, index) => {
            const widgets = _.get(component, 'content.widgets', []);
            if (!widgets) {
              return null;
            }

            const widget = widgets[0];

            switch (widget.type) {
              case SHAPE: {
                return <Shape key={widget.id} widget={widget} onCreate={handleCreateShape} />
              }

              case LINE: {
                return <Line key={widget.id} widget={widget} onCreate={handleCreateLine} />
              }

              case RECTANGLE: {
                return <Rectangle key={widget.id} widget={widget} onCreate={handleCreateShape} />
              }

              default: {
                return null;
              }
            }
          })}
        </div>
      </InfiniteScroll>
    </div>
  )
}

const Shape = ({ widget, onCreate }) => {
  return (
    <div className={styles.widget} key={widget.id} onClick={() => {
      onCreate(widget, widget.type);
    }}>
      <ReactSVG className={cx(styles.rectangle, styles.shape)} src={widget.content} />
    </div>
  )
}

const Rectangle = ({widget, onCreate}) => {
  const fill = _.get(widget, 'properties.desktop.fill');
  const type = _.get(widget, 'properties.desktop.type');

  return (
    <div className={styles.widget} key={widget.id} onClick={() => {
      onCreate(widget, widget.type);
    }}>
      <ReactSVG className={cx(styles.rectangle, styles.shape)} src='/images/shapes/rectangle.svg' />
    </div>
  )
}

const Line = ({widget, onCreate}) => {
  const stroke = _.get(widget, 'properties.desktop.stroke');
  const type = _.get(widget, 'properties.desktop.type');

  return (
    <div className={cx(styles.widget, styles.line)} key={type} onClick={() => {
      onCreate(widget);
    }}>
      <Widgets.Shapes.Line className={styles.lineShape} type={type} stroke={stroke} />
    </div>
  )
}

const BrandIconPaletteView = ({ editorSession, defaultParent, palette, mediaType = 'image' }) => {
  const dispatch = useDispatch();
  const paletteContext = React.useContext(PaletteContext);
  const storyboardContext = React.useContext(StoryboardContext);

  const mediaList = useSelector(state => { return _.get(state, `palette.brandkit[${palette}][${mediaType}].items`) || [] });
  const loading = useSelector(state => { return _.get(state, `palette.brandkit[${palette}][${mediaType}].loading`) });
  const fetched = useSelector(state => { return _.get(state, `palette.brandkit[${palette}][${mediaType}].fetched`) });
  const pagination = useSelector(state => { return _.get(state, `palette.brandkit[${palette}][${mediaType}].pagination`) });
  const hasMoreItems = _.get(pagination, 'currentPage') < _.get(pagination, 'totalPages') - 1;

  const parentWidget = useSelector(state => {
    const widgets = _.get(state, `${editorSession}.present.widgets`) || []
    return widgets.find(widget => {
      return widget.uid === defaultParent
    });
  });

  const desktopParentLayout = _.get(parentWidget, `properties.desktop.layout`);
  const mobileParentLayout = _.get(parentWidget, `properties.mobile.layout`);

  useEffect(() => {
    if (!fetched && palette) {
      handleFetchMediaForPalette(0);
    }
  }, [mediaType, palette]);

  const handleCreateImage = (media) => {
    const naturalWidth = _.get(media, 'width') || 75;
    const naturalHeight = _.get(media, 'height') || 75;

    var desktopWidth = Math.min(75, naturalWidth);
    var desktopHeight = desktopWidth * naturalHeight / naturalWidth;

    var mobileWidth = Math.min(300, naturalWidth);
    var mobileHeight = mobileWidth * naturalHeight / naturalWidth;

    const desktopPosition = getPositionForNewWidget(editorSession, desktopParentLayout, { width: desktopWidth, height: desktopHeight });
    const mobilePosition = getPositionForNewWidget(editorSession, mobileParentLayout, { width: 75, height: desktopHeight });

    var widget = {
      type: IMAGE,
      name: "Image",
      properties: {
        "desktop": {
          "visible": true,
          "layout": {
            "transform": {
              "translate": `${desktopPosition.x}px, ${desktopPosition.y}px`,
              "rotate": "0deg"
            },
            "width": `${desktopWidth}px`,
            "height": `${desktopHeight}px`
          },
          "crop": null,
          "alt": null,
          "animation": null,
          "border": null,
          "position": null,
          "shadow": null
        },
        "mobile": {
          "visible": true,
          "layout": {
            "transform": {
              "translate": `${mobilePosition.x}px, ${mobilePosition.y}px`,
              "rotate": "0deg"
            },
            "width": `${mobileWidth}px`,
            "height": `${mobileHeight}px`
          },
          "crop": null,
          "alt": null,
          "animation": null,
          "border": null,
          "position": null,
          "shadow": null
        }
      },
      content: media
    }

    dispatch(createWidget(editorSession, [widget], defaultParent));

    var mixpanelPayload = getStoryboardMixpanelEventPayload(storyboardContext.id, storyboardContext.name, storyboardContext.type);
    mixpanelPayload = {
      ...mixpanelPayload,
      widget_type: 'Icon',
      brandkit_widget: true
    }
    dispatch(createMixpanelEvent(MIXPANEL_WIDGET_USED, mixpanelPayload))


    paletteContext.close();
  }

  const handleFetchMediaForPalette = (page) => {
    const request = {
      palette: palette,
      mediaTypes: [mediaType],
      page: page,
      limit: 20,
      sort: 'modifiedDate',
      order: 'desc'
    };

    dispatch(fetchMediaForPalette(palette, 'brandkit', mediaType, request));
  }

  const loadMore = () => {
    const nextPage = pagination.currentPage + 1;
    if (nextPage >= pagination.totalPages) {
      return;
    }
    handleFetchMediaForPalette(nextPage);
  }

  return (
    <InfiniteScroll
      className={cx(styles.scrollable)}
      loading={loading}
      hasMore={hasMoreItems}
      loadMore={loadMore}
    >
      <div className={styles.brandIconGrid}>
        {mediaList.map(media => {
          return (
            <Media key={media.id}
              media={media}
              selected={false}
              onClick={() => {
                handleCreateImage(media);
              }}
            />
          )
        })}
      </div>
    </InfiniteScroll>
  )
}


export const Media = ({ media, selected, onClick }) => {
  return (
    <div className={cx(styles.mediaPalette)} onClick={onClick}>
      <div className={cx(styles.iconPalette, { [styles.selected]: selected })}>
        <IKImage path={media.url}
          transformation={[{
            "width": "600"
          }]}
          loading="lazy" />
      </div>
      <div className={styles.namePalette}>
        <span className={styles.labelPalette}>
          {media.name}
        </span>
      </div>
    </div>
  );
}